import { useEffect, useRef, useState } from "react";

/**
 * Inline text editing for a table cell: toggles between a display span and an
 * `Input`, focuses/selects on open, saves on Enter/blur, and reverts on Escape
 * or a failed save. Only calls `onSave` when the trimmed value actually changed.
 */
export function useInlineEdit(initialValue: string, onSave: (value: string) => Promise<void>) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Don't clobber an in-progress edit if the underlying value changes
    // (e.g. a concurrent update) while the user is typing.
    if (isEditing) {
      return;
    }
    setValue(initialValue);
  }, [initialValue, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== initialValue) {
      try {
        await onSave(trimmed);
      } catch {
        return;
      }
    } else {
      setValue(initialValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    } else if (e.key === "Escape") {
      setValue(initialValue);
      setIsEditing(false);
    }
  };

  return {
    isEditing,
    setIsEditing,
    value,
    setValue,
    inputRef,
    handleSave,
    handleKeyDown,
  };
}
